import { defineStore } from "pinia";
import { useMassSignatureApi } from "../http/mass-sign.api";
import { type StrictNullable } from "@pasta/box-v2";
import { MassSignatureStatus, type ReadMassSignatureDto } from "../dto/mass-signature.dto";
import { flatten } from "lodash";
import { type EnhanceOptions } from "../dto/enhance-signature.dto";
import { AlertDuration, AlertType, useAlertManager } from "@pasta/box-v2";

interface MassSignatureState {
  enabled: boolean;
  selectedLetterFiles: number[];
  selectionBeforeIsolation: StrictNullable<number[]>;
  currentMassSignature: StrictNullable<ReadMassSignatureDto>;
  maxInactivity: number;
  maxLetterFiles: number;
}

export const useMassSignatureStore = defineStore("mass-signature", {
  state: (): MassSignatureState => {
    return {
      enabled: false,
      selectedLetterFiles: [],
      selectionBeforeIsolation: null,
      currentMassSignature: null,
      maxInactivity: 70_000,
      maxLetterFiles: 100,
    };
  },
  getters: {
    totalDocs(): number {
      return flatten(this.currentMassSignature?.letterFiles.map(lf => lf.documents)).length;
    },
    totalSignPosCount(): number {
      const docs = flatten(this.currentMassSignature?.letterFiles.map(lf => lf.documents));
      return docs.reduce((acc, curr) => {
        return acc + curr.signaturePositionsCount;
      }, 0);
    },
    totalLetterFile(): number {
      return this.currentMassSignature?.letterFiles.length ?? 0;
    },
  },

  actions: {
    toggleSelection(id: number) {
      if (this.selectedLetterFiles.includes(id)) {
        this.selectedLetterFiles = this.selectedLetterFiles.filter(letterFileId => letterFileId !== id);
        return;
      }
      // same limit as the back end, which refuses bigger mass signatures
      if (this.selectedLetterFiles.length >= this.maxLetterFiles) {
        useAlertManager().createAlert({
          type: AlertType.WARNING,
          duration: AlertDuration.LONG,
          message: `La signature en bloc est limitée à ${this.maxLetterFiles} parapheurs. Désélectionnez-en un pour en ajouter un autre.`,
        });
        return;
      }
      this.selectedLetterFiles = [...this.selectedLetterFiles, id];
    },
    clearSelection() {
      this.selectedLetterFiles = [];
    },
    isolateLetterFile(id: number) {
      this.selectionBeforeIsolation ??= this.selectedLetterFiles;
      this.selectedLetterFiles = [id];
    },
    restoreSelection() {
      if (!this.selectionBeforeIsolation) return;
      this.selectedLetterFiles = this.selectionBeforeIsolation;
      this.selectionBeforeIsolation = null;
    },
    setCurrent(current: StrictNullable<ReadMassSignatureDto>) {
      this.currentMassSignature = current;
    },
    setEnabled(enabled: boolean) {
      this.enabled = enabled;
    },
    setMaxInactivity(maxInactivity: number) {
      this.maxInactivity = maxInactivity;
    },
    setMaxLetterFiles(maxLetterFiles?: number) {
      if (maxLetterFiles) this.maxLetterFiles = maxLetterFiles;
    },
    async start(pades: boolean, enhanceOptions?: EnhanceOptions) {
      const client = useMassSignatureApi();
      await client.start(this.selectedLetterFiles, pades, enhanceOptions);
      return this.getMine();
    },
    async getMine() {
      const client = useMassSignatureApi();
      this.setCurrent(await client.getMine());
      return this.currentMassSignature;
    },
    async fetchSummary() {
      const client = useMassSignatureApi();
      this.currentMassSignature = await client.getMassSignatureSummary(this.selectedLetterFiles);
      return this.currentMassSignature;
    },
    updateMassSignature(body: { document: number; status: MassSignatureStatus }) {
      if (!this.currentMassSignature) return;

      this.$patch(state => {
        const counts = {
          [MassSignatureStatus.DONE]: 0,
          [MassSignatureStatus.PROCESSING]: 0,
          [MassSignatureStatus.ERROR]: 0,
          total: 0,
        };
        state.currentMassSignature!.letterFiles.forEach(letterFile => {
          letterFile.documents.forEach(document => {
            if (document.id === body.document) {
              document.status = body.status;
            }
            counts[document.status]++;
            counts.total++;
          });
        });

        if (counts[MassSignatureStatus.PROCESSING] > 0) {
          state.currentMassSignature!.status = MassSignatureStatus.PROCESSING;
        } else if (counts[MassSignatureStatus.ERROR] > 0) {
          state.currentMassSignature!.status = MassSignatureStatus.ERROR;
        } else {
          state.currentMassSignature!.status = MassSignatureStatus.DONE;
        }
      });
    },
    async complete(next: boolean) {
      const client = useMassSignatureApi();
      await client.complete(next);
      this.removeSignedFromSelectionBeforeIsolation();
      this.currentMassSignature = null;
      this.clearSelection();
    },
    removeSignedFromSelectionBeforeIsolation() {
      if (!this.selectionBeforeIsolation) return;
      const signedIds = this.currentMassSignature?.letterFiles.map(letterFile => letterFile.id) ?? [];
      this.selectionBeforeIsolation = this.selectionBeforeIsolation.filter(id => !signedIds.includes(id));
    },
  },
});
